import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, Lock, Flower2 } from "lucide-react";

export default function WomensVitality() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/womens-vitality").then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  if (!data) {
    return <div className="min-h-screen bg-[#F9F6F0]"><Header /><p className="p-20 text-center text-[#5C6359]">…</p></div>;
  }

  const { hero, sections = [], articles = [], is_member, locked_articles = 0 } = data;

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="womens-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {hero?.image && <img src={hero.image} alt="Women's Vitality" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#2D322B]/90 via-[#2D322B]/55 to-[#2D322B]/40"></div>
        </div>
        <div className="relative px-6 sm:px-12 lg:px-20 pt-28 pb-24 max-w-5xl mx-auto text-[#F9F6F0]">
          <p className="label-eyebrow !text-[#D49A44] mb-4">{hero?.eyebrow || "Cycles & Seasons"}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="womens-hero-title">
            {hero?.title}
          </h1>
          {hero?.subtitle && <p className="mt-6 text-lg text-[#F9F6F0]/85 max-w-2xl leading-relaxed">{hero.subtitle}</p>}
          <div className="mt-10 flex flex-wrap gap-4">
            {is_member ? (
              <Link to="/biomarkers" data-testid="womens-cta-panel" className="btn-primary inline-flex items-center gap-2">
                Track your Women's Panel <ArrowUpRight className="w-4 h-4" />
              </Link>
            ) : (
              <Link to="/pricing?plan=vital" data-testid="womens-cta-join" className="btn-primary inline-flex items-center gap-2">
                Become a member <ArrowUpRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="px-6 sm:px-12 lg:px-20 py-20 max-w-6xl mx-auto space-y-16">
        {sections.map((s, i) => (
          <div key={i} className={`grid lg:grid-cols-12 gap-10 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`womens-section-${i}`}>
            {s.image ? (
              <div className="lg:col-span-5">
                <div className="rounded-3xl overflow-hidden border border-[#D1C7B8]/60">
                  <img src={s.image} alt={s.heading} className="w-full h-72 object-cover" />
                </div>
              </div>
            ) : null}
            <div className={s.image ? "lg:col-span-7" : "lg:col-span-12 max-w-3xl"}>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#2D322B] leading-tight">{s.heading}</h2>
              <p className="mt-5 text-[#5C6359] leading-relaxed whitespace-pre-line">{s.body}</p>
            </div>
          </div>
        ))}
      </section>

      {/* THEMED ARTICLES */}
      <section className="px-6 sm:px-12 lg:px-20 pb-16 max-w-6xl mx-auto">
        <p className="label-eyebrow mb-3 !text-[#3F6B45]">From the Library</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2D322B] mb-10">Writings for every season.</h2>
        {articles.length === 0 ? (
          <p className="text-sm text-[#5C6359]">New writings are being tended. Check back soon.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="womens-articles">
            {articles.map((c) => (
              <Link to={`/library/${c.id}`} key={c.id} className="group block rounded-3xl overflow-hidden bg-[#F1ECE4] border border-[#D1C7B8] hover:-translate-y-1 transition-transform" data-testid={`womens-article-${c.id}`}>
                <div className="h-48 overflow-hidden bg-[#D1C7B8]">
                  {c.image && <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-widest text-[#C05C42]">{c.category}</p>
                  <h3 className="font-serif text-xl text-[#2D322B] mt-2 leading-snug">{c.title}</h3>
                  {c.excerpt && <p className="text-sm text-[#5C6359] mt-2 leading-relaxed">{c.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* MEMBER GATE */}
      {!is_member && (
        <section className="px-6 sm:px-12 lg:px-20 pb-24 max-w-4xl mx-auto">
          <div className="rounded-3xl bg-[#2D322B] text-[#F9F6F0] p-10 sm:p-14 text-center relative overflow-hidden" data-testid="womens-member-gate">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#C05C42]/30 blur-3xl"></div>
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-[#D49A44]/20 text-[#D49A44] flex items-center justify-center mx-auto mb-6">
                <Lock className="w-5 h-5" />
              </div>
              <p className="label-eyebrow !text-[#D49A44] mb-3">Members' grove</p>
              <h2 className="font-serif text-3xl sm:text-4xl leading-snug">
                {locked_articles > 0
                  ? `${locked_articles}+ more writings inside, plus the full Women's Vitality companion.`
                  : "The full Women's Vitality companion lives inside membership."}
              </h2>
              <p className="mt-5 text-[#F9F6F0]/80 max-w-xl mx-auto leading-relaxed">
                Members unlock the complete hub — perimenopause to thriving-beyond guides, the women's biomarker panel,
                and Massey, your AI concierge, to weave it into your own vitality. Educational only — always alongside your practitioner.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <Link to="/pricing?plan=vital" data-testid="womens-gate-cta" className="btn-primary !bg-[#D49A44] hover:!bg-[#bf8a3a] !text-[#2D322B] inline-flex items-center gap-2">
                  Start with Vital <ArrowUpRight className="w-4 h-4" />
                </Link>
                {!user && (
                  <Link to="/login" className="btn-secondary !border-[#F9F6F0]/40 !text-[#F9F6F0]" data-testid="womens-gate-login">Sign in</Link>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {is_member && (
        <section className="px-6 sm:px-12 lg:px-20 pb-24 max-w-4xl mx-auto">
          <div className="rounded-3xl bg-[#F1ECE4] border border-[#D1C7B8] p-8 flex items-center gap-4" data-testid="womens-member-banner">
            <Flower2 className="w-6 h-6 text-[#C05C42] shrink-0" />
            <p className="text-sm text-[#5C6359]">
              You have full access as a member. Track your <Link to="/biomarkers" className="text-[#C05C42] hover:underline">Women's Vitality Panel</Link>, and ask <Link to="/concierge" className="text-[#C05C42] hover:underline">Massey</Link> to weave these practices into your protocols.
            </p>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
